import React from "react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AlertTriangle, AlertCircle, Clock, Building2, User, CheckCircle2, Search } from "lucide-react";

/**
 * IncidentStats — Contadores de estado (filtros rápidos) y filtros de la lista de incidencias.
 *
 * Extraído 1:1 desde `pages/IncidentsPage.js`. Cada tarjeta actúa como filtro toggle por
 * estado (Nuevas / En Tratamiento / Pend. Proveedor / Pend. Cliente / Resueltas / Total);
 * la tarjeta activa se resalta con un anillo. Incluye el buscador y el filtro por prioridad.
 *
 * @param {Object}   props.stats             Totales por estado ({ nueva, en_tratamiento, ..., total }).
 * @param {string}   props.statusFilter      Filtro de estado activo ("all" | estado).
 * @param {Function} props.setStatusFilter   Cambia/limpia el filtro de estado (toggle).
 * @param {string}   props.searchTerm        Término de búsqueda actual.
 * @param {Function} props.setSearchTerm     Setter del término de búsqueda.
 * @param {string}   props.priorityFilter    Filtro de prioridad activo ("all" | prioridad).
 * @param {Function} props.setPriorityFilter Setter del filtro de prioridad.
 */
export const IncidentStats = ({
  stats,
  statusFilter,
  setStatusFilter,
  searchTerm,
  setSearchTerm,
  priorityFilter,
  setPriorityFilter,
}) => {
  return (
    <>
      {/* Status Counters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "NUEVA" ? "ring-2 ring-red-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "NUEVA" ? "all" : "NUEVA")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Nuevas</p>
                <p className="text-xl font-bold text-red-400">{stats.nueva || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "EN_TRATAMIENTO" ? "ring-2 ring-blue-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "EN_TRATAMIENTO" ? "all" : "EN_TRATAMIENTO")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Clock className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">En Tratamiento</p>
                <p className="text-xl font-bold text-blue-400">{stats.en_tratamiento || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "PENDIENTE_PROVEEDOR" ? "ring-2 ring-purple-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "PENDIENTE_PROVEEDOR" ? "all" : "PENDIENTE_PROVEEDOR")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Pend. Proveedor</p>
                <p className="text-xl font-bold text-purple-400">{stats.pendiente_proveedor || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "PENDIENTE_CLIENTE" ? "ring-2 ring-yellow-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "PENDIENTE_CLIENTE" ? "all" : "PENDIENTE_CLIENTE")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <User className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Pend. Cliente</p>
                <p className="text-xl font-bold text-yellow-400">{stats.pendiente_cliente || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "RESUELTO" ? "ring-2 ring-green-500" : ""}`}
          onClick={() => setStatusFilter(statusFilter === "RESUELTO" ? "all" : "RESUELTO")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Resueltas</p>
                <p className="text-xl font-bold text-green-400">{stats.resuelto || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#0A0A35] border-[#1E1E5F] cursor-pointer transition-all ${statusFilter === "all" ? "ring-2 ring-[#03BEE4]" : ""}`}
          onClick={() => setStatusFilter("all")}
        >
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#03BEE4]/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-[#03BEE4]" />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Total</p>
                <p className="text-xl font-bold text-[#03BEE4]">{stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por número, título o cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#0A0A35] border-[#1E1E5F] text-white"
          />
        </div>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40 bg-[#0A0A35] border-[#1E1E5F] text-white">
            <SelectValue placeholder="Prioridad" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
            <SelectItem value="all" className="text-white">Todas</SelectItem>
            <SelectItem value="CRITICA" className="text-red-400">Crítica</SelectItem>
            <SelectItem value="ALTA" className="text-orange-400">Alta</SelectItem>
            <SelectItem value="MEDIA" className="text-blue-400">Media</SelectItem>
            <SelectItem value="BAJA" className="text-slate-400">Baja</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );
};
